import { FileExcelOutlined } from '@ant-design/icons';
import { Button, Card, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { TablePaginationConfig } from 'antd/lib/table';
import { Dayjs } from 'dayjs';
import FileSaver from 'file-saver';
import { useContext, useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import trapImageApi from '../../../apis/TrapImageApi';
import trapPlotApi from '../../../apis/TrapPlotApi';
import ConstantLabel from '../../../components/ConstantLabel/ConstantLabel';
import InsectsVariationComponent from '../../../components/InsectsVariationComponent/InsectsVariationComponent';
import CustomContext from '../../../contexts/CustomContext';
import { Page } from '../../../models/Elements';
import { CustomAuth, TrapImage, TrapPlot } from '../../../models/Entities';
import alertService from '../../../services/AlertService';
import paginationService from '../../../services/TableService';
import styles from './TrapImagesComponent.module.scss';

/**
 * Returns the dashboard trap images component. It lists the latest trap images received by the company.
 * @returns the dashboard trap images component
 */
const pageSize = 10;

const TrapImagesComponent: React.FC = () => {
    /*** HOOKS ***/

    const intl = useIntl();
    const context = useContext(CustomContext);
    const auth = context.auth as CustomAuth;
    const [trapImagesPage, setTrapImagesPage] = useState<Page<TrapImage>>();
    const [trapPlots, setTrapPlots] = useState<Map<number, TrapPlot>>(new Map());
    const [page, setPage] = useState<number>(0);
    const [sortField, setSortField] = useState<string>('date');
    const [sortOrder, setSortOrder] = useState<boolean>(false);
    const [loading, setLoading] = useState<'loading' | 'exporting'>();

    /*** EFFECTS ***/

    // list the trap plots of the company to resolve the plot of each trap image
    useEffect(() => {
        const init = async () => {
            try {
                if (auth.companyId) {
                    const trapPlotsPage = await trapPlotApi.list(0, 1000, 'id', true, undefined, undefined, 'ENABLED');
                    const trapPlots = new Map<number, TrapPlot>(trapPlotsPage.content.filter((tp) => !!tp.id).map((tp) => [tp.id!, tp]));
                    setTrapPlots(trapPlots);
                }
            } catch (error) {
                alertService.displayError(error, intl);
            }
        };
        init();
    }, [intl, auth.companyId]);

    // list the trap images of the company
    useEffect(() => {
        const init = async () => {
            try {
                setLoading('loading');
                if (auth.companyId) {
                    const trapImagesPage = await trapImageApi.list(page, pageSize, sortField, sortOrder, auth.companyId);
                    setTrapImagesPage(trapImagesPage);
                }
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [intl, auth.companyId, page, sortField, sortOrder]);

    /*** METHODS ***/

    const handleTableChange = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        setPage(pagination.current ? pagination.current - 1 : 0);
        setSortField(sorter.field);
        setSortOrder(sorter.order === 'ascend');
    };

    const exportSpreadsheet = async () => {
        try {
            setLoading('exporting');
            if (auth.companyId) {
                const spreadsheet = await trapImageApi.listSpreadsheet(sortField, sortOrder, auth.companyId);
                FileSaver.saveAs(spreadsheet, intl.formatMessage({ id: 'trapImages.spreadsheet.fileName' }));
            }
        } catch (error) {
            alertService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    const createLink = (trapImage: TrapImage): string | undefined => {
        const trapPlot = trapImage.trapPlotId ? trapPlots.get(trapImage.trapPlotId) : undefined;
        if (trapPlot?.id && trapPlot?.plot?.id) {
            return `/plots/${trapPlot.plot.id}/traps/${trapPlot.id}`;
        } else {
            return undefined;
        }
    };

    /*** VISUAL ***/

    const items = trapImagesPage ? trapImagesPage.content : [];
    const columns: ColumnsType<TrapImage> = [
        {
            title: <FormattedMessage id="trapImage.date" />,
            dataIndex: 'date',
            key: 'date',
            width: 160,
            align: 'center',
            sorter: true,
            defaultSortOrder: 'descend',
            render: (value: Dayjs | undefined) =>
                value && <FormattedDate value={value as any} year="numeric" month="2-digit" day="2-digit" hour="2-digit" minute="2-digit" />
        },
        {
            title: <FormattedMessage id="trapImage.plotId" />,
            dataIndex: 'trapPlotId',
            key: 'plot',
            width: 160,
            render: (value: number | undefined, trapImage: TrapImage) => {
                const trapPlot = value ? trapPlots.get(value) : undefined;
                const link = createLink(trapImage);
                if (link) {
                    return <Link to={link}>{trapPlot?.plot?.name}</Link>;
                } else {
                    return trapPlot?.plot?.name;
                }
            }
        },
        {
            title: <FormattedMessage id="trapImage.crop" />,
            dataIndex: 'crop',
            key: 'crop',
            width: 120,
            render: (value: string | undefined) => <ConstantLabel value={value} prefix="crop.type." />
        },
        {
            title: <FormattedMessage id="trapImage.trapUuid" />,
            dataIndex: 'trapUuid',
            key: 'trapUuid',
            width: 140,
            render: (value: string | undefined, trapImage: TrapImage) => {
                const trapPlot = trapImage.trapPlotId ? trapPlots.get(trapImage.trapPlotId) : undefined;
                const label = trapPlot?.trap?.shortUuid || value;
                const link = createLink(trapImage);
                if (link) {
                    return <Link to={link}>{label}</Link>;
                } else {
                    return label;
                }
            }
        },
        {
            title: <FormattedMessage id="trapImage.plague" />,
            dataIndex: 'plague',
            key: 'plague',
            width: 140,
            render: (value: string | undefined) => <ConstantLabel value={value} prefix="plague.type." />
        },
        {
            title: <FormattedMessage id="trapImage.insectsCount" />,
            dataIndex: 'insectsCount',
            key: 'insectsCount',
            width: 100,
            align: 'right',
            sorter: true,
            render: (value: number | undefined) => <strong>{value}</strong>
        },
        {
            title: <FormattedMessage id="trapImage.insectsCountAccumulated" />,
            dataIndex: 'insectsCountAccumulated',
            key: 'insectsCountAccumulated',
            width: 160,
            align: 'right',
            sorter: true,
            render: (value: number | undefined) => <strong>{value}</strong>
        },
        {
            title: <FormattedMessage id="trapImage.insectsVariation" />,
            dataIndex: 'insectsVariation',
            key: 'insectsVariation',
            width: 120,
            align: 'right',
            render: (value: number | undefined) => <InsectsVariationComponent insectsVariation={value} />
        }
    ];

    return (
        <div className={styles.container}>
            <Card
                bordered={false}
                className={styles.card}
                title={<FormattedMessage id="dashboard.trapImages.title" />}
                extra={
                    <Tooltip title={<FormattedMessage id="button.export" />}>
                        <Button icon={<FileExcelOutlined />} onClick={exportSpreadsheet} loading={loading === 'exporting'} type="link" />
                    </Tooltip>
                }
            >
                <Table
                    dataSource={items}
                    columns={columns}
                    pagination={paginationService.createPagination(trapImagesPage, { pageSize })}
                    rowKey="id"
                    onChange={handleTableChange}
                    sortDirections={['ascend', 'descend']}
                    showSorterTooltip={false}
                    loading={loading === 'loading'}
                    className="table"
                    scroll={{ x: 1100 }}
                />
            </Card>
        </div>
    );
};
export default TrapImagesComponent;
